import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { isAuthed } from "@/lib/api";
import CityPage from "@/pages/CityPage";
import HuntsIndex from "@/pages/HuntsIndex";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Lock, MapPin, QrCode, Sparkles, Trophy, Compass, Check, Award, Share2, ScanLine, AlertCircle, RefreshCw, MessageCircle } from "lucide-react";
import QrScanner from "@/components/QrScanner";
import confetti from "canvas-confetti";

// Deep link to WhatsApp with a pre-composed message about a single stop.
// wa.me can't attach an image via URL, so we include the hero image link
// inline and let WhatsApp render the preview card. Adding `hostitalyhub.com`
// on its own line brings recipients to the marketing home page.
function shareStopOnWhatsApp(stop, hunt) {
  const home = "https://hostitalyhub.com";
  const cityLink = `${home}/caccia-al-tesoro/${(hunt?.city || "").toLowerCase()}`;
  const parts = [
    `🗺️ ${stop.title}`,
    hunt?.city ? `_${hunt.city} — ${hunt.title || "Mappa del Tesoro"}_` : "",
    "",
    stop.teaser || "",
    stop.hero_image && stop.hero_image.startsWith("http") ? `\n📸 ${stop.hero_image}` : "",
    "",
    `Scopri la caccia completa e prenota un B&B in città:`,
    cityLink,
    `Altre città su ${home}`,
  ].filter(Boolean);
  const url = `https://wa.me/?text=${encodeURIComponent(parts.join("\n"))}`;
  window.open(url, "_blank", "noopener");
}
import { toast } from "sonner";

// Parchment aesthetic — subtle paper texture SVG data-URI (aged sepia noise)
const PARCHMENT_BG = "linear-gradient(135deg, #f5e9cf 0%, #ecd9b3 45%, #e5cea1 100%)";
const PARCHMENT_TEXTURE = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.45  0 0 0 0 0.32  0 0 0 0 0.18  0 0 0 0.18 0'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.35'/></svg>\")";
// Burnt vignette overlay so the parchment looks aged at the edges
const PARCHMENT_VIGNETTE = "radial-gradient(ellipse at center, transparent 40%, rgba(60,35,15,0.28) 100%)";
// Calligraphic serif applied to headings; falls back to the app's display font
const CALLIGRAPHIC_STACK = "'IM Fell English', 'IM Fell English SC', Georgia, serif";

function TreasureHunt() {
  // Smart dispatch for the /caccia-al-tesoro/* URL family:
  //   • no param              → full index of every published hunt
  //   • :slug starts "hunt-"  → the interactive game
  //   • otherwise             → the city SEO landing (hunt + local properties)
  const params = useParams();
  const huntId = params.huntId;
  if (!huntId) {
    return <HuntsIndex />;
  }
  if (!huntId.startsWith("hunt-")) {
    return <CityPage citySlug={huntId} />;
  }
  return <TreasureHuntGame />;
}

// Parchment-style ornamental placeholder shown when a stop image is
// unavailable or has been rejected by superuser as out-of-context. Keeps
// the vintage aesthetic instead of a broken-image icon.
const PLACEHOLDER_HERO_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'>
    <defs>
      <linearGradient id='p' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0' stop-color='#f5e9cf'/><stop offset='0.5' stop-color='#ecd9b3'/><stop offset='1' stop-color='#c9a675'/>
      </linearGradient>
    </defs>
    <rect width='400' height='300' fill='url(%23p)'/>
    <g fill='none' stroke='%238a5a2b' stroke-width='1.5' opacity='0.55'>
      <rect x='16' y='16' width='368' height='268' rx='2'/>
      <rect x='22' y='22' width='356' height='256' rx='2' stroke-width='0.6'/>
      <circle cx='200' cy='150' r='48'/>
      <path d='M200 100 L200 200 M150 150 L250 150 M164 114 L236 186 M164 186 L236 114'/>
    </g>
    <text x='200' y='250' text-anchor='middle' font-family='Georgia, serif' font-style='italic' font-size='16' fill='%238a5a2b'>· illustrazione in arrivo ·</text>
  </svg>`
)}`;

const heroSrc = (url) => (url && url.trim()) ? url : PLACEHOLDER_HERO_SVG;

function TreasureHuntGame({ huntId: propHuntId, embedded = false }) {
  const params = useParams();
  const huntId = params.huntId || propHuntId || "hunt-roma-secreta";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validateStop, setValidateStop] = useState(null);
  const [prize, setPrize] = useState(null);
  const [access, setAccess] = useState(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  // Preload the calligraphic Google Font ONCE per mount — keeps the vintage
  // headline look aligned with the parchment palette. Cleaned up on unmount so
  // other pages don't inherit the font weight.
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=IM+Fell+English:ital@0;1&family=IM+Fell+English+SC&display=swap";
    link.setAttribute("data-treasure-font", "1");
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, []);

  const load = () => {
    setLoading(true);
    api.get(`/hunts/${huntId}`).then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    if (isAuthed()) {
      api.get(`/hunts/${huntId}/access`).then((r) => {
        setAccess(r.data);
        if (r.data?.status === "needs_purchase") setShowPurchase(true);
        // Confirm purchase if returning from Stripe redirect
        const qs = new URLSearchParams(window.location.search);
        if (qs.get("purchased") === "1" && qs.get("session_id")) {
          api.post(`/hunts/${huntId}/confirm-purchase?session_id=${encodeURIComponent(qs.get("session_id"))}`)
            .then(() => { toast.success("Accesso sbloccato!"); window.history.replaceState({}, "", window.location.pathname); load(); })
            .catch(() => toast.error("Impossibile verificare il pagamento"));
        }
      }).catch(() => {});
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [huntId]);

  const startPurchase = async () => {
    setPurchasing(true);
    try {
      const r = await api.post(`/hunts/${huntId}/purchase`, {});
      if (r.data?.url && r.data.url.startsWith("http")) {
        window.location.href = r.data.url;
      } else {
        toast.success(r.data?.status === "included" ? "Accesso incluso nel tuo piano!" : "Accesso già attivo");
        setShowPurchase(false); load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore checkout");
    } finally {
      setPurchasing(false);
    }
  };

  const authed = isAuthed();
  const hunt = data?.hunt;
  const stops = data?.stops || [];
  const previewMode = !!data?.preview_mode;
  const narrative = stops.filter(s => s.path === "narrative");
  const food = stops.filter(s => s.path === "food");
  const visitedSet = useMemo(() => new Set(data?.progress?.visited_stop_ids || []), [data]);
  const progress = data?.progress;

  const openPrize = async () => {
    try {
      const r = await api.post(`/hunts/${huntId}/claim-prize`, {});
      setPrize(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  if (loading || !hunt) {
    return (
      <div className="min-h-screen bg-[#F5EFE3]">
        {!embedded && <Navbar/>}
        <div className="pt-24 text-center text-muted-foreground italic">Caricamento…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ background: PARCHMENT_BG, backgroundImage: `${PARCHMENT_VIGNETTE}, ${PARCHMENT_TEXTURE}, ${PARCHMENT_BG}`, color: "#3a2a17" }}>
      {!embedded && <Navbar/>}
      {previewMode && (
        <div className="relative z-10 border-b border-[#8a5a2b]/40" style={{ background: "rgba(60,40,20,0.92)", color: "#f5e9cf" }} data-testid="hunt-preview-banner">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-start gap-3 text-sm">
            <Lock className="w-4 h-4 shrink-0 mt-0.5"/>
            <div className="flex-1">
              <span className="italic">
                Anteprima della mappa — le storie complete, i QR di sblocco e il premio finale si attivano dopo il check-in in una struttura di <b>{hunt.city}</b>.
              </span>
            </div>
            <Link
              to={`/strutture?city=${encodeURIComponent((hunt.city || "").toLowerCase())}`}
              className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#f5e9cf] text-[#3a2a17] hover:bg-white transition-all font-semibold"
              data-testid="hunt-preview-book-cta"
            >
              Trova una struttura →
            </Link>
          </div>
        </div>
      )}
      {/* HERO — parchment map style */}
      <section className="relative overflow-hidden" data-testid="treasure-hero">
        <div className="absolute inset-0">
          <img src={hunt.hero_image} alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(60,40,20,0.55) 0%, rgba(70,45,20,0.9) 100%)" }}/>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 py-32 sm:py-44 text-center" style={{ color: "#f5e9cf" }}>
          <CompassRose className="w-14 h-14 mx-auto mb-4 opacity-90"/>
          <div className="text-[10px] uppercase tracking-[0.55em] opacity-80 mb-5">Anno Domini MMXXVI · HospitalityHub</div>
          <h1 className="font-normal italic leading-[0.9]" style={{ fontFamily: CALLIGRAPHIC_STACK, fontSize: "clamp(2.8rem, 7vw, 6rem)", letterSpacing: "0.01em", textShadow: "0 2px 20px rgba(0,0,0,0.35), 0 0 40px rgba(255,220,140,0.15)" }}>
            La Mappa del Tesoro <br/><span style={{ fontSize: "0.7em" }}>di {hunt.city}</span>
          </h1>
          <div className="w-24 h-px bg-current mx-auto my-8 opacity-60"/>
          <p className="text-lg italic max-w-2xl mx-auto opacity-95 font-display">{hunt.subtitle}</p>
          <p className="text-sm opacity-75 mt-5 max-w-xl mx-auto italic">{hunt.description}</p>
        </div>
      </section>

      {/* BOLLINI ROW — collected stamps */}
      {progress && (
        <section className="max-w-5xl mx-auto px-6 py-10" data-testid="bollini-row">
          <div className="text-center mb-6">
            <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: "#8a5a2b" }}>I tuoi bollini</div>
            <h3 className="italic text-4xl mt-2" style={{ color: "#3a2a17", fontFamily: CALLIGRAPHIC_STACK }}>
              {progress.visited_stop_ids?.length || 0} / {stops.length}
            </h3>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {stops.map((s, i) => {
              const collected = visitedSet.has(s.id);
              return (
                <div key={s.id} title={s.title} data-testid={`bollino-${s.id}`}
                     className={`w-14 h-14 rounded-full border-2 flex items-center justify-center italic text-lg transition-all ${collected ? "border-[#8a5a2b] text-[#3a2a17] shadow-lg" : "border-[#c9a675]/40 text-[#c9a675]/50"}`}
                     style={{
                       fontFamily: CALLIGRAPHIC_STACK,
                       background: collected ? "radial-gradient(circle at 30% 30%, #f7d896, #b57a2c)" : "transparent",
                       transform: collected ? `rotate(${(i * 7) % 15 - 7}deg)` : "none",
                       boxShadow: collected ? "0 4px 12px rgba(138,90,43,0.35), inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 6px rgba(0,0,0,0.15)" : "none",
                     }}>
                  {collected ? <Award className="w-6 h-6"/> : String(i + 1).padStart(2, "0")}
                </div>
              );
            })}
          </div>
          {progress.prize_unlocked && !progress.prize_claimed && (
            <div className="text-center mt-8">
              <Button onClick={openPrize} data-testid="claim-prize-btn"
                className="bg-[#8a5a2b] hover:bg-[#6d4620] text-[#f5e9cf] px-8 py-6 rounded-full italic text-lg"
                style={{ fontFamily: CALLIGRAPHIC_STACK }}>
                <Trophy className="w-4 h-4 mr-2"/> Riscatta il tuo Tesoro
              </Button>
            </div>
          )}
          {progress.prize_claimed && (
            <div className="text-center mt-6">
              <Button size="sm" variant="outline" onClick={openPrize} className="border-[#8a5a2b] text-[#8a5a2b]">Vedi il codice</Button>
            </div>
          )}
          {/* End-of-journey PDF download — always shown to signed-in guests
              from their reserved area. Public backend endpoint means it also
              works after logout, keeping the souvenir with them forever. */}
          {stops.length > 0 && (
            <div className="text-center mt-6 flex flex-col items-center gap-2" data-testid="hunt-download-map">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/hunts/${huntId}/map.pdf`}
                target="_blank" rel="noopener noreferrer"
                data-testid="hunt-download-map-btn"
                className="inline-flex items-center gap-2 border-2 border-[#8a5a2b] text-[#3a2a17] px-6 py-3 rounded-full italic hover:bg-[#8a5a2b] hover:text-[#f5e9cf] transition-all"
                style={{ fontFamily: CALLIGRAPHIC_STACK, background: "rgba(245,233,207,0.6)" }}
              >
                <Award className="w-4 h-4"/>Scarica la Mappa in PDF
              </a>
              <span className="text-[10px] italic" style={{ color: "#8a5a2b" }}>
                · i {stops.length} segreti impaginati su pergamena con QR di sincronizzazione ·
              </span>
            </div>
          )}
        </section>
      )}

      {/* NARRATIVE PATH */}
      <section className="max-w-5xl mx-auto px-6 py-24" data-testid="narrative-path">
        <div className="mb-16 text-center">
          <div className="text-[11px] uppercase tracking-[0.4em] mb-3 font-semibold" style={{ color: "#8a5a2b" }}>Capitolo primo</div>
          <h2 className="font-normal italic" style={{ fontFamily: CALLIGRAPHIC_STACK, fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "#3a2a17" }}>
            Il Percorso dei Segreti
          </h2>
          <p className="text-sm italic mt-4 max-w-lg mx-auto" style={{ color: "#6b4820" }}>
            Sette storie custodite tra le pieghe della città.
          </p>
          <OrnamentDivider/>
        </div>
        <div className="space-y-32">
          {narrative.map((stop, idx) => (
            <StopCard key={stop.id} stop={stop} index={idx} onValidate={() => setValidateStop(stop)} authed={authed} hunt={hunt}/>
          ))}
        </div>
      </section>

      {/* EXPLORER PATH */}
      <section className="border-t border-[#c9a675]/40" style={{ background: "rgba(210,175,120,0.15)" }}>
        <div className="max-w-5xl mx-auto px-6 py-24" data-testid="food-path">
          <div className="mb-16 text-center">
            <div className="text-[11px] uppercase tracking-[0.4em] mb-3 font-semibold flex items-center justify-center gap-2" style={{ color: "#8a5a2b" }}>
              <Compass className="w-3 h-3"/> Capitolo secondo
            </div>
            <h2 className="font-normal italic" style={{ fontFamily: CALLIGRAPHIC_STACK, fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "#3a2a17" }}>
              Il Percorso dell'Esploratore
            </h2>
            <p className="text-sm italic mt-4 max-w-lg mx-auto" style={{ color: "#6b4820" }}>
              Locali storici e artigiani complici del viaggio.
            </p>
            <OrnamentDivider/>
          </div>
          <div className="space-y-32">
            {food.map((stop, idx) => (
              <StopCard key={stop.id} stop={stop} index={idx} onValidate={() => setValidateStop(stop)} authed={authed} isFood hunt={hunt}/>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERBOARD */}
      <LeaderboardSection huntId={huntId}/>

      {/* PRIZE TEASER */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center" data-testid="prize-section">
        <Trophy className="w-10 h-10 mx-auto mb-6" style={{ color: "#8a5a2b" }}/>
        <div className="text-[11px] uppercase tracking-[0.4em] mb-3 font-semibold" style={{ color: "#8a5a2b" }}>Il Premio Finale</div>
        <h2 className="font-normal italic mb-6" style={{ fontFamily: CALLIGRAPHIC_STACK, fontSize: "clamp(2rem, 4vw, 3rem)", color: "#3a2a17" }}>
          {hunt.prize_title}
        </h2>
        <p className="max-w-xl mx-auto leading-relaxed italic" style={{ color: "#6b4820" }}>
          {hunt.prize_description}
        </p>
        {!authed && (
          <div className="mt-10">
            <Button onClick={() => navigate("/guest/auth")} data-testid="signup-cta"
              className="bg-[#8a5a2b] hover:bg-[#6d4620] text-[#f5e9cf] px-8 py-6 text-base rounded-full">
              Iscriviti gratis per sbloccare tutte le tappe
            </Button>
          </div>
        )}
      </section>

      <ValidateStopDialog stop={validateStop} onClose={() => setValidateStop(null)} onSuccess={() => { setValidateStop(null); load(); }} huntId={huntId}/>
      <PrizeDialog prize={prize} hunt={hunt} huntId={huntId} onClose={() => setPrize(null)}/>
      <PurchaseDialog
        open={showPurchase}
        hunt={hunt}
        access={access}
        onClose={() => setShowPurchase(false)}
        onPurchase={startPurchase}
        busy={purchasing}
      />
    </div>
  );
}

function PurchaseDialog({ open, hunt, access, onClose, onPurchase, busy }) {
  if (!open || !hunt) return null;
  const price = access?.price_eur || hunt?.one_time_price_eur || 19.90;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="purchase-dialog" style={{ background: "#f5e9cf", color: "#3a2a17" }}>
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.35em] mb-2 flex items-center gap-2" style={{ color: "#8a5a2b" }}>
            <Lock className="w-4 h-4"/> Mappa d'Autore
          </div>
          <DialogTitle className="font-display italic text-3xl">Sblocca «{hunt.title}»</DialogTitle>
        </DialogHeader>
        {hunt.hero_image && <img src={hunt.hero_image} alt="" className="w-full h-40 object-cover rounded my-3"/>}
        <p className="text-sm leading-relaxed italic" style={{ color: "#6b4820" }}>
          Questa Mappa è una <b>Mappa d'Autore</b> curata da HospitalityHub. Con il tuo piano attuale puoi:
        </p>
        <ul className="text-sm mt-3 space-y-2" style={{ color: "#3a2a17" }}>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0"/> Sbloccare tutte e 10 le tappe con storie complete</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0"/> Il tuo nome sul Registro degli Esploratori</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0"/> Certificato PDF stampabile con sigillo di cera</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0"/> Premio finale (aperitivo nel giardino segreto)</li>
        </ul>
        <div className="mt-5 p-4 border-2 border-dashed border-[#8a5a2b]/50 rounded text-center">
          <div className="text-xs uppercase tracking-widest text-[#8a5a2b] mb-1">Pagamento una tantum</div>
          <div className="font-display italic text-4xl">€ {Number(price).toFixed(2)}</div>
        </div>
        <div className="text-[11px] text-center mt-2 italic" style={{ color: "#6b4820" }}>
          Oppure passa a <a href="/pricing" className="underline">Pro o Enterprise</a> per l'accesso illimitato alle Mappe d'Autore.
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 border-[#8a5a2b] text-[#8a5a2b]">Annulla</Button>
          <Button onClick={onPurchase} disabled={busy} data-testid="purchase-checkout-btn"
            className="flex-1 bg-[#8a5a2b] hover:bg-[#6d4620] text-[#f5e9cf]">
            {busy ? "…" : `Compra ora €${Number(price).toFixed(2)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompassRose({ className }) {
  return (
    <svg className={className} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="46" stroke="currentColor" strokeWidth="1.5" opacity="0.7"/>
      <circle cx="50" cy="50" r="38" stroke="currentColor" strokeWidth="0.5" opacity="0.4"/>
      <path d="M50 8 L54 50 L50 92 L46 50 Z" fill="currentColor" opacity="0.85"/>
      <path d="M8 50 L50 46 L92 50 L50 54 Z" fill="currentColor" opacity="0.55"/>
      <circle cx="50" cy="50" r="3" fill="currentColor"/>
      <text x="50" y="18" textAnchor="middle" fontSize="8" fill="currentColor" fontFamily="serif" fontStyle="italic">N</text>
    </svg>
  );
}

function OrnamentDivider() {
  return (
    <div className="flex items-center justify-center gap-3 mt-6" aria-hidden="true">
      <div className="w-16 h-px bg-[#8a5a2b]/50"/>
      <span className="text-[#8a5a2b] text-lg font-display italic">❦</span>
      <div className="w-16 h-px bg-[#8a5a2b]/50"/>
    </div>
  );
}

function LeaderboardSection({ huntId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/hunts/${huntId}/leaderboard`).then((r) => setRows(r.data?.leaderboard || [])).catch(() => {});
  }, [huntId]);
  if (!rows.length) return null;
  const fmt = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    if (h > 24) return `${Math.floor(h / 24)}g ${h % 24}h`;
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
    return `${m}m ${String(sec).padStart(2, "0")}s`;
  };
  return (
    <section className="max-w-3xl mx-auto px-6 py-20 border-t border-[#c9a675]/40" data-testid="leaderboard-section">
      <div className="text-center mb-10">
        <div className="text-[11px] uppercase tracking-[0.4em] mb-3 font-semibold" style={{ color: "#8a5a2b" }}>Registro degli esploratori</div>
        <h2 className="font-display font-normal italic" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: "#3a2a17" }}>
          I più veloci di sempre
        </h2>
        <OrnamentDivider/>
      </div>
      <div className="border-2 border-[#8a5a2b]/40 rounded p-2 bg-[#f5e9cf]/60 shadow-inner">
        <table className="w-full">
          <tbody>
            {rows.map((r) => (
              <tr key={r.rank + r.badge_code} className="border-b border-[#c9a675]/30 last:border-0" data-testid={`leaderboard-row-${r.rank}`}>
                <td className="p-3 w-14 text-center font-display italic text-2xl" style={{ color: r.rank === 1 ? "#b8860b" : r.rank <= 3 ? "#8a5a2b" : "#a08056" }}>
                  {r.rank === 1 ? "🏆" : `${r.rank}°`}
                </td>
                <td className="p-3">
                  <div className="font-display italic text-lg" style={{ color: "#3a2a17" }}>{r.nickname}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#8a5a2b]/70">Badge #{r.badge_code}</div>
                </td>
                <td className="p-3 text-right font-mono text-sm" style={{ color: "#6b4820" }}>{fmt(r.duration_s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StopCard({ stop, index, onValidate, authed, isFood, hunt }) {
  const locked = stop.locked;
  const visited = stop.visited;
  return (
    <article
      className={`grid grid-cols-1 md:grid-cols-2 gap-10 items-center ${index % 2 === 1 ? "md:direction-rtl" : ""}`}
      data-testid={`stop-card-${stop.id}`}
    >
      <div className={`${index % 2 === 1 ? "md:order-2" : ""} relative aspect-[4/5] overflow-hidden rounded-sm shadow-xl`}>
        {stop.hero_image && (
          <img src={heroSrc(stop.hero_image)} alt="" className={`tesoro-vintage w-full h-full object-cover transition-all duration-700 ${locked ? "blur-md scale-105 grayscale" : "hover:scale-[1.03]"}`}/>
        )}
        {locked && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="text-center text-white p-6">
              <Lock className="w-8 h-8 mx-auto mb-3"/>
              <div className="text-xs uppercase tracking-widest opacity-80">Iscriviti gratis</div>
              <div className="font-display italic text-2xl mt-2">per svelare</div>
            </div>
          </div>
        )}
        {visited && (
          <div className="absolute top-4 right-4 bg-emerald-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg" title="Tappa validata">
            <Check className="w-5 h-5"/>
          </div>
        )}
      </div>
      <div className={`${index % 2 === 1 ? "md:order-1" : ""}`}>
        <div className="text-[11px] uppercase tracking-[0.35em] text-primary/70 mb-3 flex items-center gap-2">
          <span className="font-display italic text-2xl not-italic-only" style={{ fontStyle: "italic" }}>{String(index + 1).padStart(2, "0")}</span>
          <span>·</span>
          <span>{isFood ? "Tappa gastronomica" : "Segreto"}</span>
        </div>
        <h3 className="font-display font-normal italic leading-[1.05] mb-6" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
          {stop.title}
        </h3>
        {locked ? (
          <>
            <p className="text-lg font-display italic text-muted-foreground leading-relaxed mb-6">{stop.teaser}</p>
            <Link to="/guest/auth" className="inline-flex items-center gap-2 text-primary underline underline-offset-4 font-medium">
              Continua il racconto
            </Link>
          </>
        ) : (
          <>
            <p className="text-lg font-display italic text-muted-foreground leading-relaxed mb-6">{stop.teaser}</p>
            <p className="text-base leading-[1.8] mb-8" style={{ color: "#4A413A" }}>
              {stop.story}
            </p>
            {stop.sponsor_name && (
              <div className="text-xs italic text-muted-foreground mb-4">
                In collaborazione con{" "}
                <a href={stop.sponsor_url} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-4">
                  {stop.sponsor_name}
                </a>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              {authed && !visited && (
                <Button
                  onClick={onValidate}
                  data-testid={`validate-btn-${stop.id}`}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full"
                >
                  <Sparkles className="w-4 h-4 mr-2"/>
                  Sono qui — Valida la tappa
                </Button>
              )}
              {visited && (
                <div className="inline-flex items-center gap-2 text-emerald-700 text-sm">
                  <Check className="w-4 h-4"/> Tappa completata
                </div>
              )}
              <Button
                onClick={() => shareStopOnWhatsApp(stop, hunt)}
                variant="outline"
                size="sm"
                className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366] hover:text-white rounded-full"
                data-testid={`share-stop-${stop.id}`}
                title="Condividi questa tappa su WhatsApp"
              >
                <MessageCircle className="w-4 h-4 mr-1.5"/>
                Condividi tappa
              </Button>
            </div>
          </>
        )}
      </div>
    </article>
  );
}

function ValidateStopDialog({ stop, onClose, onSuccess, huntId }) {
  // Dual-factor validation UX: guest must both (a) scan the correct QR at the
  // physical stop AND (b) be within 30m of its coordinates. Both are captured
  // in the same flow — no method toggle, no manual copy-paste of tokens.
  const [qrToken, setQrToken] = useState("");
  const [gps, setGps] = useState(null);
  const [gpsErr, setGpsErr] = useState(null);
  const [scannerOn, setScannerOn] = useState(false);
  const [manual, setManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);

  // Immediately request GPS on open so the user isn't blocked at submit time.
  useEffect(() => {
    if (!stop) return;
    setQrToken(""); setGps(null); setGpsErr(null); setScannerOn(true);
    setManual(false); setLastError(null);
    if (!navigator.geolocation) { setGpsErr("GPS non disponibile su questo dispositivo"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy }),
      (err) => setGpsErr("Posizione non disponibile: " + err.message),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 }
    );
  }, [stop]);

  const canSubmit = qrToken.trim().length > 0 && gps && !busy;

  const onDecode = (text) => {
    setQrToken(text); setScannerOn(false); setLastError(null);
  };

  const submit = async () => {
    if (!stop || !canSubmit) return;
    setBusy(true); setLastError(null);
    try {
      const r = await api.post(`/hunts/${huntId}/validate`, {
        stop_id: stop.id, method: "combined",
        qr_token: qrToken.trim(), lat: gps.lat, lng: gps.lng,
      });
      if (r.data?.ok) {
        toast.success("Tappa validata!");
        onSuccess();
      } else {
        const reason = r.data?.reason;
        let msg = "Codice non valido";
        if (reason === "too_far") msg = `QR corretto, ma sei a ${Math.round(r.data.distance_m)} m dalla tappa (max ${r.data.max_distance_m} m). Avvicinati.`;
        else if (reason === "wrong_qr") msg = "Sei nel posto giusto ma il QR è sbagliato. Inquadra il QR ufficiale della tappa.";
        else if (reason === "both_wrong") msg = `Nessuno dei due riscontri combacia (dist. ${Math.round(r.data.distance_m)} m). Verifica di essere alla tappa giusta.`;
        else if (reason === "gps_missing") msg = "Attiva il GPS per la validazione.";
        setLastError(msg);
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!stop} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="validate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display italic text-2xl">Valida la tappa</DialogTitle>
          <DialogDescription>{stop?.title} — richiede QR + GPS entro 30m</DialogDescription>
        </DialogHeader>

        {/* Step 1 — QR */}
        <div className="mt-2 border border-border rounded-md p-3 space-y-2" data-testid="validate-step-qr">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center ${qrToken ? "bg-emerald-600 text-white" : "bg-primary/15 text-primary"}`}>1</span>
            <span>Scansiona il QR della tappa</span>
            {qrToken && <Check className="w-4 h-4 text-emerald-600 ml-auto"/>}
          </div>
          {scannerOn && !qrToken && (
            <QrScanner onDecode={onDecode} onClose={() => { setScannerOn(false); setManual(true); }}/>
          )}
          {!scannerOn && !qrToken && manual && (
            <div className="space-y-2">
              <Input
                value={qrToken}
                onChange={(e) => setQrToken(e.target.value)}
                placeholder="Es. ROMA-BOCCA-2026"
                data-testid="qr-input"
                autoFocus
              />
              <button onClick={() => { setScannerOn(true); setManual(false); }} className="text-xs text-primary underline" data-testid="reopen-scanner-btn">
                Riapri lo scanner
              </button>
            </div>
          )}
          {qrToken && (
            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1.5" data-testid="qr-scanned">
              <ScanLine className="w-3.5 h-3.5"/>
              <span className="font-mono truncate">{qrToken}</span>
              <button onClick={() => { setQrToken(""); setScannerOn(true); }} className="ml-auto text-emerald-700 hover:text-emerald-900" aria-label="Ripeti">
                <RefreshCw className="w-3 h-3"/>
              </button>
            </div>
          )}
          {!scannerOn && !manual && !qrToken && (
            <button onClick={() => setManual(true)} className="text-xs text-primary underline">
              Inserisci il codice manualmente
            </button>
          )}
        </div>

        {/* Step 2 — GPS */}
        <div className="border border-border rounded-md p-3 space-y-2" data-testid="validate-step-gps">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center ${gps ? "bg-emerald-600 text-white" : gpsErr ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`}>2</span>
            <span>Rileva la tua posizione</span>
            {gps && <Check className="w-4 h-4 text-emerald-600 ml-auto"/>}
          </div>
          {gps ? (
            <div className="text-xs text-emerald-700 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1.5 flex items-center gap-2" data-testid="gps-ok">
              <MapPin className="w-3.5 h-3.5"/>
              {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}
              {gps.acc && <span className="ml-auto opacity-70">±{Math.round(gps.acc)}m</span>}
            </div>
          ) : gpsErr ? (
            <div className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded px-2 py-1.5 flex items-center gap-2" data-testid="gps-err">
              <AlertCircle className="w-3.5 h-3.5 shrink-0"/>{gpsErr}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <RefreshCw className="w-3 h-3 animate-spin"/> Rilevamento posizione…
            </div>
          )}
        </div>

        {lastError && (
          <div className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded px-2 py-2 flex items-start gap-2" data-testid="validate-last-error">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5"/>{lastError}
          </div>
        )}

        <div className="flex gap-2 mt-1">
          <Button variant="outline" onClick={onClose} className="flex-1">Annulla</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="submit-validate-btn"
            className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {busy ? "Verifica…" : "Valida"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Named export enables in-place render inside a Guest Dashboard modal without
// leaving the booking flow — see GuestDashboard.jsx `openHuntId` state.
export { TreasureHuntGame };

function PrizeDialog({ prize, hunt, huntId, onClose }) {
  const [nickname, setNickname] = useState("");
  const [saved, setSaved] = useState(false);
  const [phone, setPhone] = useState("");
  const [waSent, setWaSent] = useState(false);
  const [fomo, setFomo] = useState(null);

  // Fire a confetti burst the moment the prize card opens. Uses two waves so
  // it lasts long enough to be seen without overwhelming the screen.
  useEffect(() => {
    if (!prize) return;
    const defaults = { origin: { y: 0.35 }, colors: ["#8a5a2b", "#f7d896", "#a13a2a", "#f5e9cf", "#3a2a17"] };
    confetti({ ...defaults, particleCount: 90, spread: 70, startVelocity: 45 });
    const t = setTimeout(() => {
      confetti({ ...defaults, particleCount: 60, spread: 100, decay: 0.94, scalar: 0.9, origin: { x: 0.15, y: 0.4 } });
      confetti({ ...defaults, particleCount: 60, spread: 100, decay: 0.94, scalar: 0.9, origin: { x: 0.85, y: 0.4 } });
    }, 350);
    // Social-proof FOMO counter — how many travellers grabbed a badge this
    // week. Best-effort fetch: on any error we silently hide the widget so a
    // Mongo hiccup doesn't ruin the celebration.
    api.get(`/hunt-badges/weekly-stats?hunt_id=${huntId}`)
      .then((r) => setFomo(r.data)).catch(() => setFomo(null));
    return () => clearTimeout(t);
  }, [prize, huntId]);

  if (!prize) return null;
  const copy = () => { navigator.clipboard.writeText(prize.prize_code); toast.success("Codice copiato"); };
  const saveNick = async () => {
    if (!nickname.trim()) return;
    try {
      await api.post(`/hunts/${huntId}/nickname`, { nickname: nickname.trim() });
      setSaved(true);
      toast.success("Sei ora nel Registro degli Esploratori");
    } catch (e) { toast.error("Errore"); }
  };
  const shareUrl = `${window.location.origin}/caccia-al-tesoro/${huntId}`;
  const shareText = `Ho completato "${hunt?.title}" e vinto il tesoro! 🏆 #${(prize.prize_code || "").slice(3, 9)} — Provaci anche tu:`;
  const share = async () => {
    const payload = { title: hunt?.title, text: shareText, url: shareUrl };
    if (navigator.share) { try { await navigator.share(payload); return; } catch {} }
    navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
    toast.success("Copiato per la condivisione");
  };

  // WhatsApp notification: dopo aver capturato il numero, apriamo wa.me con
  // il messaggio pre-compilato (il guest tap "Invia" e riceve il premio come
  // chat con se stesso). Backend logga anche il numero per audit e per un
  // eventuale invio proattivo via Twilio quando le credenziali saranno attive.
  const sendWa = async () => {
    const clean = phone.replace(/[^\d+]/g, "");
    if (!clean.match(/^\+?\d{8,15}$/)) { toast.error("Numero non valido — includi il prefisso internazionale"); return; }
    const number = clean.startsWith("+") ? clean.slice(1) : clean;
    try {
      await api.post(`/hunts/${huntId}/notify-whatsapp`, { phone: clean, prize_code: prize.prize_code });
    } catch { /* non-blocking: user can still send via wa.me link */ }
    const body = `🏆 Premio Mappa del Tesoro di ${hunt?.city || ""}\n\n"${prize.prize_title || hunt?.prize_title || ""}"\n\n${prize.prize_description || hunt?.prize_description || ""}\n\nIl tuo codice: ${prize.prize_code}\n\nRiapri la mappa: ${shareUrl}`;
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(body)}`, "_blank");
    setWaSent(true);
    toast.success("Aperto WhatsApp — invia il messaggio a te stesso");
  };

  return (
    <Dialog open={!!prize} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="prize-dialog" style={{ background: "#f5e9cf", color: "#3a2a17" }}>
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.35em] mb-2 flex items-center gap-2" style={{ color: "#8a5a2b" }}>
            <Trophy className="w-4 h-4"/> Il Tesoro è tuo
          </div>
          <DialogTitle className="font-display italic text-3xl">{prize.prize_title || hunt.prize_title}</DialogTitle>
        </DialogHeader>
        {prize.prize_image && <img src={prize.prize_image} alt="" className="w-full h-48 object-cover rounded my-4"/>}
        <p className="text-sm leading-relaxed italic" style={{ color: "#6b4820" }}>{prize.prize_description || hunt.prize_description}</p>
        <div className="mt-5 p-4 border-2 border-dashed border-[#8a5a2b]/50 rounded text-center bg-[#f5e9cf]">
          <div className="text-xs uppercase tracking-widest text-[#8a5a2b] mb-2">Il tuo codice</div>
          <div className="font-mono text-2xl font-bold" data-testid="prize-code">{prize.prize_code}</div>
          <Button onClick={copy} variant="outline" size="sm" className="mt-3 border-[#8a5a2b] text-[#8a5a2b]">Copia codice</Button>
        </div>

        {/* WhatsApp handoff — richiesto esplicito, sostituisce l'email */}
        <div className="mt-5 p-3 border border-[#25D366]/40 bg-[#25D366]/5 rounded" data-testid="prize-wa-block">
          <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest" style={{ color: "#128C7E" }}>
            <MessageCircle className="w-3.5 h-3.5"/> Ricevi il premio su WhatsApp
          </div>
          {waSent ? (
            <div className="text-xs text-emerald-700 flex items-center gap-1.5" data-testid="wa-sent">
              <Check className="w-3.5 h-3.5"/> WhatsApp aperto con il tuo premio pre-compilato
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+39 333 1234567"
                inputMode="tel"
                className="bg-white/70 border-[#25D366]/40"
                data-testid="wa-phone-input"
              />
              <Button onClick={sendWa} data-testid="wa-send-btn"
                className="bg-[#25D366] hover:bg-[#128C7E] text-white">
                <MessageCircle className="w-4 h-4 mr-1"/>Invia
              </Button>
            </div>
          )}
          <div className="text-[10px] italic mt-2 text-[#128C7E]/80">
            Include prefisso internazionale (+39, +33, ecc.). Niente e-mail: la notifica arriva solo su WhatsApp.
          </div>
        </div>

        {!saved && (
          <div className="mt-5">
            <label className="text-[11px] uppercase tracking-widest text-[#8a5a2b] mb-2 block">Firma il registro (opzionale)</label>
            <div className="flex gap-2">
              <Input value={nickname} onChange={(e) => setNickname(e.target.value)} maxLength={24}
                placeholder="Il tuo pseudonimo" data-testid="nickname-input" className="bg-white/60 border-[#8a5a2b]/40"/>
              <Button onClick={saveNick} disabled={!nickname.trim()} data-testid="save-nickname-btn"
                className="bg-[#8a5a2b] hover:bg-[#6d4620] text-[#f5e9cf]">Firma</Button>
            </div>
          </div>
        )}
        <Button onClick={share} data-testid="share-badge-btn"
          className="mt-4 w-full bg-[#8a5a2b] hover:bg-[#6d4620] text-[#f5e9cf]">
          <Share2 className="w-4 h-4 mr-2"/> Condividi il badge
        </Button>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/hunts/${huntId}/completion-badge.png${nickname.trim() ? `?name=${encodeURIComponent(nickname.trim())}` : ""}`}
          target="_blank" rel="noreferrer"
          data-testid="download-instagram-badge-btn"
          className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white hover:opacity-90 transition-all font-medium"
        >
          <Trophy className="w-4 h-4"/> Scarica il badge per Instagram
        </a>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/hunts/${huntId}/certificate.pdf`}
          target="_blank" rel="noreferrer"
          data-testid="download-certificate-btn"
          className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-[#8a5a2b] text-[#8a5a2b] rounded-md hover:bg-[#8a5a2b] hover:text-[#f5e9cf] transition-all font-display italic"
        >
          <Award className="w-4 h-4"/> Scarica il certificato
        </a>
        {fomo && (fomo.global?.this_week > 0 || fomo.hunt?.this_week > 0) && (
          <div
            className="mt-4 flex items-start gap-3 p-3 rounded border border-[#8a5a2b]/30 bg-[#f7ecd0]/60"
            data-testid="fomo-badge-counter"
          >
            <div className="flex -space-x-1.5 shrink-0 pt-0.5">
              {["#a13a2a", "#8a5a2b", "#c55a3c"].map((c, i) => (
                <span key={i} className="w-6 h-6 rounded-full ring-2 ring-[#f5e9cf] flex items-center justify-center text-[10px] font-bold text-white" style={{ background: c }}>
                  {["A", "M", "L"][i]}
                </span>
              ))}
            </div>
            <div className="text-[13px] leading-snug italic text-[#3a2a17]">
              <span className="font-bold not-italic" data-testid="fomo-count">
                {fomo.global?.this_week ?? 0}
              </span>
              {" "}viaggiatori hanno scaricato il loro badge questa settimana
              {fomo.hunt?.this_week > 0 && (
                <span className="not-italic">
                  {" · "}
                  <span className="text-[#a13a2a] font-bold" data-testid="fomo-hunt-count">
                    {fomo.hunt.this_week}
                  </span>
                  {" su questa mappa"}
                </span>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
export default TreasureHunt;
